/**
 * Pure OKLCH helpers shared by ThemeService (runtime theming) and the
 * host branding-settings preview.
 */

export interface Oklch {
  l: number; // 0..100 (percent)
  c: number; // chroma
  h: number; // 0..360
}

const OKLCH_REGEX = /oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)\s*\)/i;

const DARK_LIGHTNESS_RATIO = 0.67;
const DARK_CHROMA_RATIO = 0.67;

const CONTENT_LIGHTNESS_THRESHOLD = 60;
const CONTENT_LIGHTNESS_LIGHT_FG = 95;
const CONTENT_LIGHTNESS_DARK_FG = 15;
const CONTENT_CHROMA = 0.02;

export function parseOklch(value: string): Oklch | undefined {
  const match = value.match(OKLCH_REGEX);
  if (!match) {
    return undefined;
  }
  const [, lRaw, percent, cRaw, hRaw] = match;
  const lNum = parseFloat(lRaw);
  return {
    l: percent ? lNum : lNum * 100,
    c: parseFloat(cRaw),
    h: parseFloat(hRaw),
  };
}

export function formatOklch({ l, c, h }: Oklch): string {
  return `oklch(${round(l, 2)}% ${round(c, 4)} ${round(h, 2)})`;
}

export function darkenOklch(value: string): string {
  const parsed = parseOklch(value);
  if (!parsed) {
    return value;
  }
  return formatOklch({
    l: clamp(parsed.l * DARK_LIGHTNESS_RATIO, 0, 100),
    c: Math.max(0, parsed.c * DARK_CHROMA_RATIO),
    h: parsed.h,
  });
}

/**
 * One step of a designed colour ramp, expressed *relative* to a base.
 *
 * Lightness and hue shift by an offset while chroma scales by a ratio,
 * because that is how the ramps in `data-viz-colors` actually behave: they
 * lighten and drift hue while chroma falls away. Relative rather than
 * absolute so the same ramp can be re-anchored on a white-label primary.
 */
export interface OklchStep {
  /** Added to lightness (percentage points). */
  dl: number;
  /** Multiplied into chroma. */
  cRatio: number;
  /** Added to hue (degrees). */
  dh: number;
}

/**
 * Re-anchor a designed ramp step on a new base colour.
 *
 * Returns undefined when the base is not OKLCH — a white-label primary may
 * arrive as a hex, and silently treating an unparseable colour as the brand
 * default would paint an agency's charts in someone else's brand.
 */
export function shiftOklch(
  value: string,
  { dl, cRatio, dh }: OklchStep
): string | undefined {
  const parsed = parseOklch(value);
  if (!parsed) {
    return undefined;
  }
  return formatOklch({
    l: clamp(parsed.l + dl, 0, 100),
    c: Math.max(0, parsed.c * cRatio),
    // Hue is circular: a ramp that drifts past 360 wraps rather than clamps.
    h: (((parsed.h + dh) % 360) + 360) % 360,
  });
}

/**
 * Convert OKLCH to an sRGB hex string.
 *
 * Needed because renderers parse a narrower set of colours than CSS does:
 * AG Charts' `Color.fromString` takes only `#hex`, a named colour, or a
 * string containing `rgb`, and **throws** on `oklch(...)`. The browser cannot
 * be asked to do this either — Chrome's computed style and `canvas`
 * `fillStyle` both *preserve* `oklch()` per CSS Color 4 rather than
 * converting to sRGB — so the transform is done here.
 *
 * This is the published OKLab→linear-sRGB matrix, not an approximation; the
 * spec pins it against the design palette's own hexes, which it reproduces
 * exactly. Being pure also means a headless caller (the scheduled email
 * export) converts colours identically to the browser.
 *
 * Out-of-gamut results are clipped per channel. A very high-chroma
 * white-label colour therefore lands on the nearest edge of sRGB rather than
 * being rejected — a slightly duller colour beats an unpainted chart.
 */
export function oklchToHex(value: string): string | undefined {
  const parsed = parseOklch(value);
  if (!parsed) {
    return undefined;
  }
  const lightness = parsed.l / 100;
  const hueRadians = (parsed.h * Math.PI) / 180;
  const a = parsed.c * Math.cos(hueRadians);
  const b = parsed.c * Math.sin(hueRadians);

  const lRoot = lightness + 0.3963377774 * a + 0.2158037573 * b;
  const mRoot = lightness - 0.1055613458 * a - 0.0638541728 * b;
  const sRoot = lightness - 0.0894841775 * a - 1.291485548 * b;
  const l = lRoot ** 3;
  const m = mRoot ** 3;
  const s = sRoot ** 3;

  const red = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const green = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const blue = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

  return `#${[red, green, blue].map(toHexChannel).join('')}`;
}

/** Linear-light channel to a gamma-encoded, gamut-clipped hex pair. */
function toHexChannel(channel: number): string {
  const encoded =
    channel <= 0.0031308
      ? 12.92 * channel
      : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055;
  const byte = Math.round(clamp(encoded, 0, 1) * 255);
  return byte.toString(16).padStart(2, '0').toUpperCase();
}

export function contentColorForOklch(value: string): string | undefined {
  const parsed = parseOklch(value);
  if (!parsed) {
    return undefined;
  }
  const l =
    parsed.l > CONTENT_LIGHTNESS_THRESHOLD
      ? CONTENT_LIGHTNESS_DARK_FG
      : CONTENT_LIGHTNESS_LIGHT_FG;
  return formatOklch({ l, c: CONTENT_CHROMA, h: parsed.h });
}

function clamp(n: number, lo: number, hi: number): number {
  if (n < lo) {
    return lo;
  }
  if (n > hi) {
    return hi;
  }
  return n;
}

function round(n: number, digits: number): number {
  const factor = Math.pow(10, digits);
  return Math.round(n * factor) / factor;
}
